import React, { useState, useEffect } from "react";
import { CheckCircle2, Grid, Save, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const RBACMatrixPage: React.FC = () => {
  const actions = ["Read", "Create", "Edit", "Delete", "Approve", "Export", "Analytics", "AI Copilot"];
  const defaultRoles = [
    "Super Admin",
    "Administrator / Registrar",
    "Head of Department (HOD)",
    "Faculty / Professor",
    "Student",
  ];

  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>({
    "Super Admin": { Read: true, Create: true, Edit: true, Delete: true, Approve: true, Export: true, Analytics: true, "AI Copilot": true },
    "Administrator / Registrar": { Read: true, Create: true, Edit: true, Delete: false, Approve: true, Export: true, Analytics: true, "AI Copilot": true },
    "Head of Department (HOD)": { Read: true, Create: true, Edit: true, Delete: false, Approve: true, Export: true, Analytics: true, "AI Copilot": false },
    "Faculty / Professor": { Read: true, Create: true, Edit: true, Delete: false, Approve: false, Export: false, Analytics: false, "AI Copilot": false },
    Student: { Read: true, Create: false, Edit: false, Delete: false, Approve: false, Export: false, Analytics: false, "AI Copilot": false },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("rbac_matrix");
      if (res.data && Object.keys(res.data).length > 0) {
        setMatrix(res.data);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load RBAC matrix.");
    } finally {
      setLoading(false);
    }
  };

  const toggleCheck = (role: string, act: string) => {
    setSaved(false);
    setMatrix((prev) => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [act]: !prev[role]?.[act],
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("rbac_matrix", matrix);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save RBAC permission matrix.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading RBAC permission matrix...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Grid className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Global RBAC Permission Matrix Configuration</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Matrix Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Permissions</span>
            </>
          )}
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-950">
        <table className="w-full text-center text-xs text-slate-300">
          <thead className="bg-slate-900 text-[10px] font-mono uppercase text-slate-400 border-b border-slate-800">
            <tr>
              <th className="p-3 text-left">Role Name</th>
              {actions.map((act) => (
                <th key={act} className="p-3">
                  {act}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 font-mono">
            {defaultRoles.map((roleName) => (
              <tr key={roleName} className="hover:bg-slate-850">
                <td className="p-3 text-left font-sans font-bold text-slate-100">{roleName}</td>
                {actions.map((act) => {
                  const checked = matrix[roleName]?.[act] ?? false;
                  return (
                    <td key={act} className="p-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCheck(roleName, act)}
                        className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
